import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';

import styles from './Button.module.css';

/**
 * Варианты DESIGN_SYSTEM §5.1. Красный зовёт к главному действию, синий
 * отмечает действия на карточках — других акцентов у кнопки нет:
 *
 * * `primary` — **главное действие экрана**, на публичных страницах и в `/app/*`
 *   одинаково: заливка `--color-cta`, белый текст. Правило «одна красная кнопка
 *   на экран» соблюдает экран, а не примитив;
 * * `outline-action` — **действия на карточках** («Details ansehen», выбор
 *   автомобиля на четвёрке): прозрачная, граница `--color-accent`, текст
 *   `--color-accent-text`;
 * * `secondary` — второстепенное действие: белая, граница `--color-border-strong`,
 *   тёмный текст;
 * * `ghost` — третичные действия и действия внутри строк: без фона и границы;
 * * `danger` — деструктивные действия: белая, граница и текст `--color-danger`,
 *   **без заливки** — кнопку, которая что-то ломает, нельзя спутать с главной;
 * * `inverse` — на тёмных поверхностях (шапка, CTA-полоса).
 *
 * Прежнего варианта `brand` нет: «красная только для публичных страниц» слилась
 * с главной кнопкой — это `primary`.
 */
export type ButtonVariant =
  'primary' | 'secondary' | 'outline-action' | 'ghost' | 'danger' | 'inverse';

/** 32 / 40 / 48 px, на телефоне 30 / 36 / 42 (DESIGN_SYSTEM §5.1, `--control-height-*`). */
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: styles.primary ?? '',
  secondary: styles.secondary ?? '',
  'outline-action': styles.outlineAction ?? '',
  ghost: styles.ghost ?? '',
  danger: styles.danger ?? '',
  inverse: styles.inverse ?? '',
};

interface CommonProps {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  fullWidth?: boolean | undefined;
  iconEnd?: ReactNode | undefined;
  className?: string | undefined;
}

function classes({ variant = 'primary', size = 'md', fullWidth, className }: CommonProps) {
  return [
    styles.button,
    VARIANT_CLASS[variant],
    styles[size],
    fullWidth && styles.fullWidth,
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * Кнопка. Отключённая кнопка не молчит: рядом или в `title` всегда конкретная
 * причина (DESIGN_SYSTEM §5.1) — это обязанность вызывающего экрана.
 *
 * Подпись держится в одну строку, пока кнопке хватает места, и переносится,
 * когда кнопка шире своего контейнера: на 370 px немецкая подпись иначе
 * распирает колонку и даёт горизонтальный скролл страницы.
 */
export function Button({
  variant,
  size,
  fullWidth,
  iconEnd,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={classes({ variant, size, fullWidth, className })} {...rest}>
      <span className={styles.label}>{children}</span>
      {iconEnd ? <span className={styles.icon}>{iconEnd}</span> : null}
    </button>
  );
}

type ButtonLinkProps = CommonProps & LinkProps;

/** Ссылка, оформленная как кнопка: остаётся ссылкой для скринридеров и Cmd+клика. */
export function ButtonLink({
  variant,
  size,
  fullWidth,
  iconEnd,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link className={classes({ variant, size, fullWidth, className })} {...rest}>
      <span className={styles.label}>{children}</span>
      {iconEnd ? <span className={styles.icon}>{iconEnd}</span> : null}
    </Link>
  );
}
